import React from 'react';
import ApolloClient, { DocumentNode, Operation } from 'apollo-boost';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory';
import Amplify, { Auth } from 'aws-amplify';
import WebFont from 'webfontloader';
import keyframes from 'jss-keyframes';
import * as Sentry from '@sentry/browser';
import { Styles } from 'react-jss';
import { isNil } from 'ramda';
import { toast } from 'react-toastify';
import ToastContainer from 'components/ToastContainer';

import { resolvers, defaults, typeDefs } from 'state';
import { UserType } from 'components/UserMgmt/UserMgmt.context';

type Theme = {
  deepPurple: string;
  violet: string;
  softViolet: string;
  seafoamGreen: string;
  softSeafoamGreen: string;
  coral: string;
  softCoral: string;
  softGray: string;
  darkGray: string;
  yellow: string;
  btnGray: string;
  disabledBtnGray: string;
  error: {
    background: string;
    color: string;
    borderColor: string;
  };
};

type ClientStateConfig = {
  resolvers: Record<string, unknown>;
  defaults: Record<string, unknown>;
  typeDefs?: string | DocumentNode;
};

const clientState: ClientStateConfig = {
  resolvers,
  defaults,
  typeDefs,
};

const oauth = {
  scopes: [
    'aws.cognito.signin.user.admin',
    'email',
    'openid',
    'phone',
    'profile',
  ],
  domain: process.env.COGNITO_DOMAIN,
  redirectSignIn: process.env.COGNITO_REDIRECT_SIGN_IN,
  redirectSignOut: process.env.COGNITO_REDIRECT_SIGN_OUT,
  responseType: process.env.COGNITO_RESPONSE_TYPE,
};

Amplify.configure({
  oauth,
  region: process.env.AWS_REGION,
  userPoolId: process.env.COGNITO_USER_POOL_ID,
  userPoolWebClientId: process.env.COGNITO_USER_POOL_WEB_CLIENT_ID,
  Auth: {
    identityPoolId: process.env.COGNITO_IDENTITY_POOL_ID,
  },
  Storage: {
    customPrefix: {
      public: '',
    },
  },
});

const request = async (operation: Operation) => {
  try {
    const response = await Auth.currentSession();
    const idToken = response.getIdToken();

    if (idToken) {
      operation.setContext({
        headers: {
          Authorization: `Bearer ${idToken.jwtToken}`,
        },
      });
    }
  } catch (error) {
    console.error('graphql request: ', error);
  }
};

const headers = {
  Accept: 'application/json',
  'content-type': 'application/json; charset=utf-8',
};

// generated by Fragment Matcher plugin
import introspectionResult from './fragmentTypes';

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData: introspectionResult,
});

const cache = new InMemoryCache({
  fragmentMatcher,
});

const config: any = {
  request,
  headers,
  clientState,
  cache,
  uri: process.env.API_URL,
  onError: ({ networkError, graphQLErrors }: any) => {
    if (networkError) {
      toast(
        () => (
          <ToastContainer
            type="error"
            title="Network Error"
            description={`Check your connection and try again.`}
          />
        ),
        { hideProgressBar: true, closeButton: false },
      );
    } else {
      const error = graphQLErrors[0];
      toast(
        () => (
          <ToastContainer
            type="error"
            title="GraphQL error"
            description={`${error.displayMessage} Please try again later.`}
          />
        ),
        { hideProgressBar: true, closeButton: false },
      );
    }
  },
};

const client = new ApolloClient(config);

const theme: Theme = {
  deepPurple: '#120B33',
  violet: '#683CAF',
  softViolet: '#C9C3EF',
  seafoamGreen: '#8FE2B4',
  softSeafoamGreen: '#CCEFDB',
  coral: '#F4897D',
  softCoral: '#F1C4BF',
  softGray: '#E1E8ED',
  darkGray: '#767676',
  yellow: '#ffe45f',
  btnGray: '#a9b8c2',
  disabledBtnGray: '#d8dee1',
  error: {
    background: '#fff6f6 !important',
    color: '#9f3a38 !important',
    borderColor: '#e0b4b4 !important',
  },
};

window.zESettings = {
  webWidget: {
    color: { theme: theme.violet },
    contactForm: {
      title: {
        '*': 'Feedback',
      },
      supress: true,
    },
  },
};

const opacityAnimation = keyframes({
  '0%': { opacity: 0.2 },
  '25%': { opacity: 0.4 },
  '50%': { opacity: 0.6 },
  '75%': { opacity: 0.8 },
  '100%': { opacity: 1 },
});

const jssGlobal: Partial<Styles> = {
  '@global html': {
    fontSize: '16px !important',
  },
  '@global body': {
    fontSize: '16px !important',
    background: '#f7f8f9 !important',
  },
  '@global #app': {
    height: '100%',
  },
  '@global .security': {
    height: '100%',
  },
  '@global *:not(i)': {
    fontFamily: 'DOSH !important',
    fontWeight: 'normal',
  },
  '@global label': {
    fontSize: '14px !important',
    color: '#646464 !important',
    fontWeight: '500 !important' as 'lighter',
  },
  '@global .ui.header': {
    fontWeight: '500 !important' as 'normal',
  },
  '@global svg': {
    overflow: 'visible !important',
  },
  '@global .datamap': {
    position: 'relative !important' as 'relative',
  },
  '@global .react-datepicker__day--outside-month': {
    opacity: 0,
    pointerEvents: 'none',
  },
  '@global text.recharts-text.recharts-cartesian-axis-tick-value': {
    fontSize: '.750rem',
    fill: theme.darkGray,
  },
  '@global line.recharts-cartesian-axis-line': {
    stroke: theme.darkGray,
  },
  '@global .sentry-error-embed-wrapper': {
    zIndex: '9999 !important',
  } as any,
  '@global .mapboxgl-ctrl-logo, .mapboxgl-ctrl-attrib, .mq-result-icon': {
    display: 'none !important',
  },
  '@global .mapbogl-popup': {
    zIndex: 1,
  },
  '@global .mapbogl-map': {
    borderRadius: '.3125rem',
    boxShadow: '0 0.125rem 0.375rem 0 rgba(0, 0, 0, 0.05)',
  },
  '@font-face': [
    {
      fontFamily: 'DOSH',
      src: `url('https://${process.env.ASSET_BUCKET}.dosh.cash/webapps/fonts/MarkOT.woff') format('woff')`,
    },
    {
      fontFamily: 'DOSH',
      src: `url('https://${process.env.ASSET_BUCKET}.dosh.cash/webapps/fonts/MarkOT-Medium.woff') format('woff')`,
      fontWeight: 500,
    },
    {
      fontFamily: 'DOSH',
      src: `url('https://${process.env.ASSET_BUCKET}.dosh.cash/webapps/fonts/MarkOT-Bold.woff') format('woff')`,
      fontWeight: 'bold',
    },
  ] as any,
};

WebFont.load({
  google: {
    families: ['Montserrat', 'PT Mono'],
  },
});

const updateZESettings = (suppress: boolean) => {
  window.zE('webWidget', 'updateSettings', {
    webWidget: {
      contactForm: {
        suppress,
      },
    },
  });
};

const zendeskInit = (userType: UserType) => {
  const suppress = isNil(userType) || userType === 'dosh';

  if (process.env.STAGE === 'prod') {
    updateZESettings(suppress);
  }
};

const sentryInit = () => {
  if (process.env.STAGE === 'prod') {
    Sentry.init({
      dsn: 'https://0472241a31884200b56544a856b0a8e2@sentry.io/1846374',
      sampleRate: 0.75,
    });
  }
};

export {
  theme,
  Theme,
  jssGlobal,
  client,
  opacityAnimation,
  sentryInit,
  zendeskInit,
};
